import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Plus, Copy, ExternalLink, DollarSign, TrendingUp, X, Check, Trash2, Eye, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const AffiliateManager = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [payoutModal, setPayoutModal] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutNotes, setPayoutNotes] = useState("");
  const [form, setForm] = useState({
    name: "", slug: "", email: "", coupon_code: "",
    discount_type: "percent", discount_value: "35",
    commission_type: "percent", commission_value: "20",
    commission_min_days: 30,
    eligible_tiers: ["foundations", "accelerator"],
  });

  useEffect(() => { fetchAffiliates(); }, []);

  const fetchAffiliates = async () => {
    try {
      const res = await fetch(`${API}/affiliates/dashboard`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAffiliates(data.affiliates || []);
      }
    } catch (err) {
      toast.error("Failed to load affiliates");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name || !form.slug || !form.coupon_code || !form.discount_value) {
      toast.error("Fill in all required fields");
      return;
    }
    try {
      const res = await fetch(`${API}/affiliates/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...form,
          discount_value: parseFloat(form.discount_value),
          commission_value: parseFloat(form.commission_value) || 0,
          commission_min_days: parseInt(form.commission_min_days) || 30,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Affiliate created!");
        setShowCreate(false);
        setForm({ name: "", slug: "", email: "", coupon_code: "", discount_type: "percent", discount_value: "35", commission_type: "percent", commission_value: "20", commission_min_days: 30, eligible_tiers: ["foundations", "accelerator"] });
        fetchAffiliates();
      } else {
        toast.error(data.detail || "Failed to create affiliate");
      }
    } catch (err) {
      toast.error("Something went wrong");
    }
  };

  const handleToggleActive = async (aff) => {
    try {
      const res = await fetch(`${API}/affiliates/${aff.affiliate_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ active: !aff.active }),
      });
      if (res.ok) {
        toast.success(aff.active ? "Affiliate deactivated" : "Affiliate reactivated");
        fetchAffiliates();
      }
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const handleRecordPayout = async () => {
    if (!payoutAmount || parseFloat(payoutAmount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    try {
      const res = await fetch(`${API}/affiliates/${payoutModal}/payouts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ amount: parseFloat(payoutAmount), notes: payoutNotes }),
      });
      if (res.ok) {
        toast.success("Payout recorded!");
        setPayoutModal(null);
        setPayoutAmount("");
        setPayoutNotes("");
        fetchAffiliates();
      }
    } catch (err) {
      toast.error("Failed to record payout");
    }
  };

  const copyLink = (slug) => {
    const link = `${window.location.origin}/?ref=${slug}`;
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied!");
  };

  const autoSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 20);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="affiliate-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-xl font-bold">Influencer Affiliates</h3>
          <p className="text-text-muted text-sm mt-1">Track referrals, commissions, and payouts</p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          data-testid="add-affiliate-btn"
        >
          <Plus className="w-4 h-4" /> Add Affiliate
        </button>
      </div>

      {/* Create Form */}
      <AnimatePresence>
        {showCreate && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-surface border border-border rounded-xl overflow-hidden"
          >
            <div className="p-6 space-y-4">
              <h4 className="font-semibold text-lg">New Affiliate</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-text-muted block mb-1">Name *</label>
                  <input
                    value={form.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setForm(f => ({ ...f, name, slug: f.slug || autoSlug(name) }));
                    }}
                    placeholder="Jake Martinez"
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-white"
                    data-testid="affiliate-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-text-muted block mb-1">Email (optional)</label>
                  <input
                    value={form.email}
                    onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="jake@email.com"
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-white"
                    data-testid="affiliate-email-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-text-muted block mb-1">Referral Slug *</label>
                  <div className="flex items-center gap-2">
                    <span className="text-text-muted text-sm whitespace-nowrap">?ref=</span>
                    <input
                      value={form.slug}
                      onChange={(e) => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))}
                      placeholder="jake"
                      className="w-full bg-background border border-border rounded-lg px-3 py-2 text-white"
                      data-testid="affiliate-slug-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-text-muted block mb-1">Coupon Code *</label>
                  <input
                    value={form.coupon_code}
                    onChange={(e) => setForm(f => ({ ...f, coupon_code: e.target.value.toUpperCase() }))}
                    placeholder="JAKE20"
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-white uppercase"
                    data-testid="affiliate-coupon-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-text-muted block mb-1">Discount</label>
                  <div className="flex gap-2">
                    <select
                      value={form.discount_type}
                      onChange={(e) => setForm(f => ({ ...f, discount_type: e.target.value }))}
                      className="bg-background border border-border rounded-lg px-3 py-2 text-white"
                      data-testid="affiliate-discount-type"
                    >
                      <option value="percent">% Off</option>
                      <option value="fixed">$ Off</option>
                    </select>
                    <input
                      type="number"
                      value={form.discount_value}
                      onChange={(e) => setForm(f => ({ ...f, discount_value: e.target.value }))}
                      placeholder={form.discount_type === "percent" ? "20" : "5.00"}
                      className="w-full bg-background border border-border rounded-lg px-3 py-2 text-white"
                      data-testid="affiliate-discount-value"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-text-muted block mb-1">Commission</label>
                  <div className="flex gap-2">
                    <select
                      value={form.commission_type}
                      onChange={(e) => setForm(f => ({ ...f, commission_type: e.target.value }))}
                      className="bg-background border border-border rounded-lg px-3 py-2 text-white"
                      data-testid="affiliate-commission-type"
                    >
                      <option value="percent">% of Revenue</option>
                      <option value="flat">$ Flat/User</option>
                    </select>
                    <input
                      type="number"
                      value={form.commission_value}
                      onChange={(e) => setForm(f => ({ ...f, commission_value: e.target.value }))}
                      placeholder={form.commission_type === "percent" ? "20" : "10"}
                      className="w-full bg-background border border-border rounded-lg px-3 py-2 text-white"
                      data-testid="affiliate-commission-value"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Eligible tiers (which subscriptions can use this code)</label>
                  <div className="flex gap-3">
                    {["foundations", "accelerator"].map(t => (
                      <label key={t} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer border ${form.eligible_tiers.includes(t) ? "bg-primary/15 border-primary text-white" : "bg-background border-border text-text-secondary"}`}>
                        <input type="checkbox" className="hidden" checked={form.eligible_tiers.includes(t)}
                          onChange={() => setForm(f => ({ ...f, eligible_tiers: f.eligible_tiers.includes(t) ? f.eligible_tiers.filter(x => x !== t) : [...f.eligible_tiers, t] }))}
                          data-testid={`affiliate-tier-${t}`} />
                        <span className="capitalize text-sm font-medium">{t}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleCreate}
                  className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                  data-testid="save-affiliate-btn"
                >
                  Create Affiliate
                </button>
                <button
                  onClick={() => setShowCreate(false)}
                  className="text-text-secondary hover:text-white px-4 py-2 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary Stats */}
      {affiliates.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-surface border border-border rounded-xl p-4">
            <p className="text-text-muted text-xs">Total Affiliates</p>
            <p className="text-2xl font-bold">{affiliates.length}</p>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4">
            <p className="text-text-muted text-xs">Total Referrals</p>
            <p className="text-2xl font-bold text-primary">{affiliates.reduce((s, a) => s + (a.total_referrals || 0), 0)}</p>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4">
            <p className="text-text-muted text-xs">Revenue from Referrals</p>
            <p className="text-2xl font-bold text-green-400">${affiliates.reduce((s, a) => s + (a.total_revenue || 0), 0).toFixed(2)}</p>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4">
            <p className="text-text-muted text-xs">Commission Owed</p>
            <p className="text-2xl font-bold text-amber-400">${affiliates.reduce((s, a) => s + (a.commission_owed || 0), 0).toFixed(2)}</p>
          </div>
        </div>
      )}

      {/* Affiliates Table */}
      {affiliates.length === 0 ? (
        <div className="bg-surface border border-border rounded-xl p-12 text-center">
          <Users className="w-12 h-12 text-text-muted mx-auto mb-3" />
          <p className="text-text-secondary">No affiliates yet</p>
          <p className="text-text-muted text-sm mt-1">Add your first influencer to start tracking referrals</p>
        </div>
      ) : (
        <div className="space-y-3">
          {affiliates.map((aff) => (
            <div key={aff.affiliate_id} className={`bg-surface border rounded-xl overflow-hidden transition-colors ${aff.active ? "border-border" : "border-red-500/30 opacity-60"}`}>
              {/* Row */}
              <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => setExpandedId(expandedId === aff.affiliate_id ? null : aff.affiliate_id)} data-testid={`affiliate-row-${aff.slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{aff.name}</span>
                    {!aff.active && <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Inactive</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-text-muted">
                    <span className="font-mono bg-background px-2 py-0.5 rounded">{aff.coupon_code}</span>
                    <span>{aff.discount_display}</span>
                    <span>{aff.commission_value}% commission</span>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-center">
                  <div>
                    <p className="text-lg font-bold text-primary">{aff.active_referrals || 0}</p>
                    <p className="text-xs text-text-muted">Active</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-text-secondary">{aff.churned_referrals || 0}</p>
                    <p className="text-xs text-text-muted">Churned</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-green-400">${(aff.total_revenue || 0).toFixed(2)}</p>
                    <p className="text-xs text-text-muted">Revenue</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-amber-400">${(aff.commission_owed || 0).toFixed(2)}</p>
                    <p className="text-xs text-text-muted">Owed</p>
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); copyLink(aff.slug); }}
                  className="p-2 text-text-muted hover:text-primary transition-colors"
                  title="Copy referral link"
                  data-testid={`copy-link-${aff.slug}`}
                >
                  <Copy className="w-4 h-4" />
                </button>
                {expandedId === aff.affiliate_id ? <ChevronUp className="w-5 h-5 text-text-muted" /> : <ChevronDown className="w-5 h-5 text-text-muted" />}
              </div>

              {/* Expanded Details */}
              <AnimatePresence>
                {expandedId === aff.affiliate_id && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 pt-2 border-t border-border space-y-4">
                      {/* Referral Link */}
                      <div className="flex items-center gap-2 bg-background rounded-lg p-3">
                        <ExternalLink className="w-4 h-4 text-text-muted flex-shrink-0" />
                        <code className="text-sm text-primary flex-1 truncate">
                          {window.location.origin}/pricing?ref={aff.slug}
                        </code>
                        <button
                          onClick={() => copyLink(aff.slug)}
                          className="text-xs bg-primary/20 text-primary px-3 py-1 rounded hover:bg-primary/30 transition-colors"
                        >
                          Copy
                        </button>
                      </div>

                      {/* Details Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div><span className="text-text-muted">Email:</span> <span className="ml-1">{aff.email || "—"}</span></div>
                        <div><span className="text-text-muted">Discount:</span> <span className="ml-1">{aff.discount_display} (forever)</span></div>
                        <div><span className="text-text-muted">Commission:</span> <span className="ml-1">{aff.commission_value}% recurring</span></div>
                        <div><span className="text-text-muted">Min Days:</span> <span className="ml-1">{aff.commission_min_days || 30}d hold</span></div>
                        <div><span className="text-text-muted">Total Paid:</span> <span className="ml-1 text-green-400">${(aff.total_paid || 0).toFixed(2)}</span></div>
                        <div><span className="text-text-muted">Total Commission:</span> <span className="ml-1">${(aff.total_commission || 0).toFixed(2)}</span></div>
                        <div><span className="text-text-muted">Tiers:</span> <span className="ml-1 capitalize">{(aff.eligible_tiers || []).join(", ")}</span></div>
                        <div><span className="text-text-muted">Created:</span> <span className="ml-1">{new Date(aff.created_at).toLocaleDateString()}</span></div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => { setPayoutModal(aff.affiliate_id); setPayoutAmount(""); setPayoutNotes(""); }}
                          className="flex items-center gap-1.5 bg-green-500/20 text-green-400 px-4 py-2 rounded-lg hover:bg-green-500/30 transition-colors text-sm"
                          data-testid={`record-payout-${aff.slug}`}
                        >
                          <DollarSign className="w-4 h-4" /> Record Payout
                        </button>
                        <button
                          onClick={() => handleToggleActive(aff)}
                          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm transition-colors ${
                            aff.active
                              ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                              : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                          }`}
                          data-testid={`toggle-active-${aff.slug}`}
                        >
                          {aff.active ? <><X className="w-4 h-4" /> Deactivate</> : <><Check className="w-4 h-4" /> Reactivate</>}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}

      {/* Payout Modal */}
      {payoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-surface border border-border rounded-xl p-6 w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-lg">Record Payout</h4>
              <button onClick={() => setPayoutModal(null)} className="text-text-muted hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-text-muted block mb-1">Amount ($)</label>
                <input
                  type="number"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-white"
                  data-testid="payout-amount-input"
                />
              </div>
              <div>
                <label className="text-sm text-text-muted block mb-1">Notes (optional)</label>
                <input
                  value={payoutNotes}
                  onChange={(e) => setPayoutNotes(e.target.value)}
                  placeholder="e.g., April payout via Venmo"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-white"
                  data-testid="payout-notes-input"
                />
              </div>
              <button
                onClick={handleRecordPayout}
                className="w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition-colors font-semibold"
                data-testid="confirm-payout-btn"
              >
                Confirm Payout
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AffiliateManager;
